import { useMemo, useCallback } from 'react';

const useTableTabs = ({
  tabOptions,
  pageSize,
  filters,
  sorting,
  updateParams,
}) => {
  const areArraysEqual = (arr1, arr2) => {
    if (!Array.isArray(arr1) || !Array.isArray(arr2)) return false;
    if (arr1.length !== arr2.length) return false;

    const sorted1 = [...arr1].sort();
    const sorted2 = [...arr2].sort();
    return JSON.stringify(sorted1) === JSON.stringify(sorted2);
  };

  const activeTabKeys = useMemo(() => {
    if (!tabOptions) return [];

    // Preset
    if (tabOptions.isPreset) {
      return tabOptions.tabs
        .filter(tab => {
          if (!tab.preset) return false;

          const filterKeys = Object.keys(filters);
          const presetKeys = Object.keys(tab.preset);

          if (filterKeys.length !== presetKeys.length) return false;

          return presetKeys.every(key => {
            const presetValue = tab.preset[key];
            const filterValue = filters[key];

            return Array.isArray(presetValue)
              ? areArraysEqual(presetValue, filterValue)
              : presetValue == filterValue;
          });
        })
        .map(tab => tab.key);
    }

    // Not Preset
    const currentValues = filters[tabOptions.field];

    // multiSelect
    if (tabOptions?.multiSelect) {
      if (!currentValues) return [];
      const values = Array.isArray(currentValues)
        ? currentValues
        : [currentValues];
      return tabOptions.tabs
        .filter(tab => values.includes(tab.value))
        .map(tab => tab.key);
    }

    // Not multiSelect
    const tab = tabOptions.tabs.find(tab => tab.value == currentValues);
    return tab ? [tab.key] : [tabOptions.tabs[0].key];
  }, [tabOptions, filters]);

  const handleTabSelect = useCallback(
    tabKey => {
      if (!tabOptions) return;

      const selectedTab = tabOptions.tabs.find(tab => tab.key === tabKey);
      if (!selectedTab) return;

      // Preset
      if (tabOptions.isPreset) {
        if (selectedTab.preset) {
          updateParams({
            pageIndex: 0,
            pageSize,
            filters: { ...selectedTab.preset },
            sorting: selectedTab.defaultSorting || sorting,
          });
        }
        return;
      }

      // Not Preset
      const newFilters = { ...filters };
      delete newFilters[tabOptions.field];

      if (selectedTab.value !== undefined) {
        newFilters[tabOptions.field] = selectedTab.value;
      }

      updateParams({ pageIndex: 0, pageSize, filters: newFilters, sorting });
    },
    [tabOptions, pageSize, filters, sorting, updateParams],
  );

  return {
    activeTabKeys,
    handleTabSelect,
  };
};

export default useTableTabs;
